<template>
  <div class="sleeve-picker" :class="tone">
    <button
      type="button"
      class="sleeve-tile random"
      :class="{ selected: duelPrefs.sleeveId === RANDOM_SLEEVE_ID }"
      :aria-pressed="duelPrefs.sleeveId === RANDOM_SLEEVE_ID"
      @click="duelPrefs.setSleeveId(RANDOM_SLEEVE_ID)"
    >
      <span class="sleeve-dice" aria-hidden="true">⚄</span>
      <span>
        <span class="sleeve-name">{{ t('settings.sleeveRandom') }}</span>
        <span class="sleeve-sub">{{ t('settings.sleeveRandomHint') }}</span>
      </span>
    </button>

    <fieldset class="sleeve-grid">
      <legend class="visually-hidden">{{ t('settings.sleeve') }}</legend>
      <button
        v-for="sleeve in sleeves"
        :key="sleeve.id"
        type="button"
        class="sleeve-tile"
        :class="{ selected: duelPrefs.sleeveId === sleeve.id }"
        :aria-pressed="duelPrefs.sleeveId === sleeve.id"
        :title="t(`settings.sleeves.${sleeve.id}`)"
        :aria-label="t(`settings.sleeves.${sleeve.id}`)"
        :disabled="locked(sleeve.id)"
        @click="pick(sleeve.id)"
      >
        <img :src="sleeve.src" alt="" />
      </button>
    </fieldset>

    <p class="sleeve-heading">{{ t('settings.leagueRewards') }}</p>
    <fieldset class="sleeve-grid">
      <legend class="visually-hidden">{{ t('settings.leagueRewards') }}</legend>
      <button
        v-for="sleeve in leagueSleeves"
        :key="sleeve.id"
        type="button"
        class="sleeve-tile league"
        :class="{ selected: duelPrefs.sleeveId === sleeve.id }"
        :aria-pressed="duelPrefs.sleeveId === sleeve.id"
        :title="tileTitle(sleeve.id)"
        :aria-label="tileTitle(sleeve.id)"
        :disabled="locked(sleeve.id)"
        @click="pick(sleeve.id)"
      >
        <img :src="sleeve.src" alt="" />
        <img
          v-if="locked(sleeve.id)"
          class="league-lock"
          :src="badgeOf(sleeve.id)"
          alt=""
        />
      </button>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { LAST_CALL_SLEEVE_ID, leagueBadgeSrc, leagueOfSleeve } from '@shared/index'
import { BASE_SLEEVES, LEAGUE_SLEEVES, RANDOM_SLEEVE_ID } from '../game/sleeves'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { useLastCallStore } from '../stores/lastCall'
import { useLeagueRewardsStore } from '../stores/leagueRewards'
import { useI18n } from '../i18n/t'

withDefaults(
  defineProps<{ tone?: 'duel' | 'menu' }>(),
  { tone: 'duel' },
)

const { t } = useI18n()
const duelPrefs = useDuelPrefsStore()
const lastCall = useLastCallStore()
const leagueRewards = useLeagueRewardsStore()
const sleeves = BASE_SLEEVES
const leagueSleeves = LEAGUE_SLEEVES

function locked(id: string): boolean {
  if (id === LAST_CALL_SLEEVE_ID) return !lastCall.sleeveUnlocked
  return !leagueRewards.sleeveUnlocked(id)
}

function badgeOf(id: string): string {
  return leagueBadgeSrc(leagueOfSleeve(id)) ?? ''
}

function tileTitle(id: string): string {
  const name = t(`settings.sleeves.${id}`)
  const league = leagueOfSleeve(id)
  if (!league || !locked(id)) return name
  return `${name} · ${t('settings.leagueLocked', { league: t(`rank.leagues.${league}`) })}`
}

function pick(id: string): void {
  if (locked(id)) return
  duelPrefs.setSleeveId(id)
}
</script>

<style scoped>
.sleeve-picker {
  width: 100%;
}

.sleeve-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(56px, 1fr));
  gap: 6px;
  margin: 0;
  padding: 0;
  border: 0;
}

.sleeve-tile {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 4px;
  border: 2px solid transparent;
  border-radius: 8px;
  background: #1e293b;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.sleeve-tile:disabled {
  cursor: default;
  opacity: 0.45;
}

.menu .sleeve-tile {
  background: var(--md-sys-color-surface-container, #1e293b);
}

.sleeve-tile img {
  display: block;
  width: 100%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border-radius: 4px;
}

.sleeve-tile.selected {
  border-color: #e8c547;
}

.sleeve-tile.league {
  position: relative;
}

.sleeve-tile.league:disabled {
  opacity: 1;
}

.sleeve-tile.league:disabled img:first-child {
  opacity: 0.4;
}

.sleeve-tile .league-lock {
  position: absolute;
  right: 2px;
  bottom: 2px;
  width: 45%;
  aspect-ratio: 1;
  object-fit: contain;
}

.sleeve-heading {
  margin: 12px 0 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #94a3b8;
}

.menu .sleeve-heading {
  color: var(--md-sys-color-on-surface-variant, #94a3b8);
}

.sleeve-tile.random {
  width: 100%;
  margin-bottom: 8px;
  padding: 8px;
  text-align: left;
}

.sleeve-dice {
  font-size: 22px;
  line-height: 1;
}

.sleeve-name {
  display: block;
  font-size: 13px;
  font-weight: 500;
}

.sleeve-sub {
  display: block;
  font-size: 11px;
  color: #94a3b8;
}

.menu .sleeve-sub {
  color: var(--md-sys-color-on-surface-variant, #94a3b8);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
